import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { ArrowForwardIconSvg } from '../../../../lib/icons';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useStepper } from '../../partials/actionStepperPartial';
import { EmployeeBonusSelectionTable } from '../../../../lib/components/tables/employee/employeeBonusSelection';
import { useActions } from '../../../../hooks/useActions';
import { HintBox } from '../../../../lib/components/atomic/typography/hintBox';
import { useFormik } from 'formik';
import { employeeAssignmentSchema } from '../utils/validationSchemas';
import { useCreateBonus } from '../hooks/useCreateBonus';
import { useStore } from '../../../../hooks/useStore';
import { useListEmployees } from '../../../../queries/employee';
import { useRetroSpecialOccasionCount } from '../../../../queries/bonus';
import { enqueueSnackbar } from 'notistack';
import { FeedbackAlert } from '../../../../lib/components/atomic/feedback';
import { ContentDialog } from '../../../../lib/components/compound/dialogs/contentDialog';
import { Drawer } from '../../../../lib/components/atomic/dataDisplay/drawer';
import { InfoSection } from '../../../../lib/components/atomic/typography/InfoSection';

export const EmployeeAssignment = observer(() => {
  const { t } = useTranslation();
  const { newBonus } = useCreateBonus();
  const { companyStore } = useStore();

  const { selectedEmployees, setSelectedEmployees } = useActions();
  const selectedEmployeeIds = useMemo(
    () => Object.keys(selectedEmployees ?? {}),
    [selectedEmployees]
  );

  const {
    setNextButtonConfig,
    activeStep,
    setActiveStep,
    showInfoDrawer,
    setShowInfoDrawer,
    editMode
  } = useStepper();

  const [continueRegardlessDialog, setContinueRegardlessDialog] = useState(false);
  const [hasTriedToSubmit, setHasTriedToSubmit] = useState(false);

  const { data: employeesData = { pages: [], pageParams: [] } } = useListEmployees(
    companyStore.company?.id,
    {
      enabled: !!companyStore.company?.id
    }
  );

  const flattenedData = useMemo(() => employeesData.pages.flat(), [employeesData]);

  const { values, errors, submitForm } = useFormik({
    initialValues: {
      specialOccasionType: newBonus.specialOccasionType,
      frequency: newBonus.frequency,
      employees: selectedEmployeeIds
    },
    validationSchema: employeeAssignmentSchema(t),
    enableReinitialize: true,
    onSubmit: (values) => {
      if (!missingBirthDateError && !missingEntryDateError) {
        newBonus.employees = values.employees;
        editMode ? setActiveStep(6) : setActiveStep(activeStep + 1);
      } else {
        setContinueRegardlessDialog(true);
      }
    }
  });

  const missingBirthDateEmployees = useMemo(() => {
    return flattenedData.filter((emp) => emp.id && selectedEmployees[emp.id] && !emp.birth_date);
  }, [flattenedData, selectedEmployees]);

  const missingEntryDateEmployees = useMemo(() => {
    return flattenedData.filter((emp) => emp.id && selectedEmployees[emp.id] && !emp.entry_date);
  }, [flattenedData, selectedEmployees]);

  const missingBirthDateError = useMemo(
    () => newBonus.specialOccasionType === 'BIRTHDAY' && missingBirthDateEmployees.length > 0,
    [newBonus.specialOccasionType, missingBirthDateEmployees.length]
  );

  const missingEntryDateError = useMemo(
    () => newBonus.specialOccasionType === 'ANNIVERSARY' && missingEntryDateEmployees.length > 0,
    [newBonus.specialOccasionType, missingEntryDateEmployees.length]
  );

  const referenceMonth = newBonus.payoutMonth ?? newBonus.startAccountingPeriod;
  const isCurrentMonth = useMemo(() => {
    if (!referenceMonth) return false;
    const today = new Date();
    return (
      referenceMonth.getFullYear() === today.getFullYear() &&
      referenceMonth.getMonth() === today.getMonth()
    );
  }, [referenceMonth]);

  const { data: retroOccasionData } = useRetroSpecialOccasionCount({
    specialOccasionType: newBonus.specialOccasionType as string,
    employeeIds: selectedEmployeeIds,
    anniversaryInterval: newBonus.anniversaryInterval as number,
    enabled: isCurrentMonth
  });
  const retroOccasionCount = retroOccasionData?.count ?? 0;

  const missingEmployeeSelection = hasTriedToSubmit && !!errors.employees;

  useEffect(() => {
    if (editMode && newBonus.employees?.length && Object.keys(selectedEmployees).length === 0) {
      const selection = newBonus.employees.reduce(
        (acc: Record<string, boolean>, id: string) => {
          acc[id] = true;
          return acc;
        },
        {} as Record<string, boolean>
      );
      setSelectedEmployees(selection);
    }
  }, [editMode, newBonus.employees, setSelectedEmployees]);

  useEffect(() => {
    newBonus.employees = selectedEmployeeIds;
  }, [selectedEmployeeIds, newBonus]);

  const handleRemoveEmployeesWithMissingData = () => {
    const employeesToRemove = missingBirthDateError
      ? missingBirthDateEmployees
      : missingEntryDateEmployees;

    const updatedSelection = { ...selectedEmployees };
    employeesToRemove.forEach((emp) => {
      if (emp.id) {
        delete updatedSelection[emp.id];
      }
    });

    setSelectedEmployees(updatedSelection);

    enqueueSnackbar(
      t('bonus:employeeRemoved', {
        count: employeesToRemove.length
      }),
      { variant: 'success' }
    );

    return updatedSelection;
  };

  const getEmployeesWithMissingData = () => {
    return missingBirthDateError
      ? missingBirthDateEmployees
      : missingEntryDateError
        ? missingEntryDateEmployees
        : [];
  };

  const getMissingEmployeesText = () => {
    if (missingBirthDateError) {
      return t('bonus:missingBirthDateEmployees', {
        count: missingBirthDateEmployees.length
      });
    }

    if (missingEntryDateError) {
      return t('bonus:missingEntryDateEmployees', {
        count: missingEntryDateEmployees.length
      });
    }

    return t('export:ERROR_NO_EMPLOYEE_SELECTED');
  };

  const handleConfirm = () => {
    const remainingEmployees = handleRemoveEmployeesWithMissingData();
    const remainingIds = Object.keys(remainingEmployees);

    if (remainingIds.length === 0) {
      setContinueRegardlessDialog(false);
      return;
    }

    newBonus.employees = remainingIds;
    setContinueRegardlessDialog(false);
    editMode ? setActiveStep(6) : setActiveStep(activeStep + 1);
  };

  const nextButtonConfig = useMemo(
    () => ({
      text: editMode ? t('common:CONFIRM') : t('common:NEXT'),
      endIcon: <ArrowForwardIconSvg />,
      onClick: () => {
        setHasTriedToSubmit(true);
        submitForm();
      }
    }),
    [editMode, t, submitForm]
  );

  useEffect(() => {
    setNextButtonConfig(nextButtonConfig);
  }, [nextButtonConfig, setNextButtonConfig]);

  return (
    <>
      <Grid container direction="row" md={12} paddingTop={'-8px'}>
        <Grid container md={12}>
          <Grid item xs={12} md={12}>
            {retroOccasionCount > 0 && (
              <Box className="pb-4">
                <FeedbackAlert
                  description={t('bonus:retroOccasionHint', {
                    count: retroOccasionCount,
                    context: values.specialOccasionType === 'BIRTHDAY' ? 'birthday' : 'anniversary'
                  })}
                  severity="info"
                />
              </Box>
            )}
            {(missingBirthDateError || missingEntryDateError || missingEmployeeSelection) && (
              <Box className="pb-4">
                <FeedbackAlert
                  description={getMissingEmployeesText()}
                  severity={missingBirthDateError || missingEntryDateError ? 'warning' : 'error'}
                  actionElement={
                    missingBirthDateError || missingEntryDateError ? (
                      <Button
                        onClick={handleRemoveEmployeesWithMissingData}
                        className="text-info-main text-xs font-normal underline hover:bg-transparent pl-42"
                      >
                        {t('employee:REMOVE_FROM_SELECTION', {
                          count: missingBirthDateError
                            ? missingBirthDateEmployees.length
                            : missingEntryDateEmployees.length
                        })}
                      </Button>
                    ) : undefined
                  }
                />
              </Box>
            )}
            {values.specialOccasionType === 'BIRTHDAY' && values.frequency === 'ONE_TIME' && (
              <FeedbackAlert
                description={
                  newBonus.editingId
                    ? t('bonus:alerts:editBirthdayOneTime')
                    : t('bonus:alerts:createBirthdayOneTime')
                }
                severity="warning"
              />
            )}
            <EmployeeBonusSelectionTable
              employeesWithMissingData={getEmployeesWithMissingData()}
              editProfilePage={missingBirthDateError ? 'personal' : 'position'}
            />
          </Grid>
        </Grid>
      </Grid>
      <ContentDialog
        open={continueRegardlessDialog}
        title={t('bonus:notConsideredEmployees')}
        onClose={() => setContinueRegardlessDialog(false)}
        onTertiaryButtonClick={() => setContinueRegardlessDialog(false)}
        primaryButton={t('common:continueRegardless')}
        tertiaryButton={t('common:CANCEL')}
        onPrimaryButtonClick={handleConfirm}
      >
        <>
          <Typography>{getMissingEmployeesText()}</Typography>
          <Typography>{t('bonus:continueRegardlessText')}</Typography>
        </>
      </ContentDialog>
      <Drawer
        label={t('bonus:employeeAssignmentDrawer:title')}
        open={showInfoDrawer}
        closeDrawer={() => setShowInfoDrawer(false)}
      >
        <div className="flex flex-col gap-y-6">
          <InfoSection
            content={t('bonus:employeeAssignmentDrawer:texts', { returnObjects: true })}
          />
          <HintBox
            description={t('bonus:employeeAssignmentDrawer:hint', { returnObjects: true })}
          />
        </div>
      </Drawer>
    </>
  );
});
